import React, { useState, useEffect } from 'react';
import Modal from '../../components/WhatsAppChat/WaModal';
import { api } from '../../services/api';

interface CreateTicketData {
  title: string;
  description: string;
  category: string;
  priority: string;
  assignee: string;
  client: string;
  dueDate: string;
  estimatedHours: number;
}

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTicketData) => void;
}

interface IUsuarioSimple {
  id: number;
  nome: string;
}

interface IEmpresaSimple {
  id: number;
  nome: string;
  razao_social: string;
}

const categorias = [
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'contabil', label: 'Contabil' },
  { value: 'pessoal', label: 'Depto. Pessoal' },
  { value: 'societario', label: 'Societario' },
  { value: 'consultoria', label: 'Consultoria' },
  { value: 'outros', label: 'Outros' },
];

const prioridades = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export default function CreateTicketModal({ isOpen, onClose, onSubmit }: CreateTicketModalProps) {
  const [agentes, setAgentes] = useState<IUsuarioSimple[]>([]);
  const [clientes, setClientes] = useState<IEmpresaSimple[]>([]);
  const [loadingAgentes, setLoadingAgentes] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [form, setForm] = useState<CreateTicketData>({
    title: '',
    description: '',
    category: 'fiscal',
    priority: 'medium',
    assignee: '',
    client: '',
    dueDate: '',
    estimatedHours: 1,
  });

  useEffect(() => {
    if (!isOpen) return;

    setLoadingAgentes(true);
    api
      .get<{ data: IUsuarioSimple[] }>('/api/v1/usuarios/?size=100')
      .then(res => {
        const lista = res.data?.data ?? [];
        setAgentes(lista);
        setForm(prev => ({ ...prev, assignee: prev.assignee || (lista[0]?.nome ?? '') }));
      })
      .catch(() => {})
      .finally(() => setLoadingAgentes(false));

    setLoadingClientes(true);
    api
      .get<{ data: IEmpresaSimple[] }>('/api/v1/empresas/?size=50')
      .then(res => {
        const lista = res.data?.data ?? [];
        setClientes(lista);
        setForm(prev => ({
          ...prev,
          client: prev.client || lista[0]?.razao_social || lista[0]?.nome || '',
        }));
      })
      .catch(() => {})
      .finally(() => setLoadingClientes(false));
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'estimatedHours' ? Number(value) : value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
    setForm({
      title: '',
      description: '',
      category: 'fiscal',
      priority: 'medium',
      assignee: agentes[0]?.nome ?? '',
      client: clientes[0]?.razao_social || clientes[0]?.nome || '',
      dueDate: '',
      estimatedHours: 1,
    });
    onClose();
  };

  const inputClass =
    'w-full px-4 py-2.5 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-wa-500/50 border-0 shadow-wa-apple transition-all';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo Ticket" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Titulo */}
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="ticket-title" className={labelClass}>
            Titulo
          </label>
          <input
            id="ticket-title"
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Ex: Declaracao IRPF - Cliente"
            required
            className={inputClass}
          />
        </div>

        {/* Descricao */}
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="ticket-description" className={labelClass}>
            Descricao
          </label>
          <textarea
            id="ticket-description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Descreva a atividade em detalhes..."
            rows={3}
            className={inputClass}
          />
        </div>

        {/* Categoria + Prioridade */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-category" className={labelClass}>
              Categoria
            </label>
            <select
              id="ticket-category"
              name="category"
              value={form.category}
              onChange={handleChange}
              className={inputClass}
            >
              {categorias.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-priority" className={labelClass}>
              Prioridade
            </label>
            <select
              id="ticket-priority"
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className={inputClass}
            >
              {prioridades.map(p => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Responsavel + Cliente */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-assignee" className={labelClass}>
              Responsavel
            </label>
            <select
              id="ticket-assignee"
              name="assignee"
              value={form.assignee}
              onChange={handleChange}
              disabled={loadingAgentes}
              className={inputClass}
            >
              {loadingAgentes && <option value="">Carregando...</option>}
              {agentes.map(a => (
                <option key={a.id} value={a.nome}>
                  {a.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-client" className={labelClass}>
              Cliente
            </label>
            <select
              id="ticket-client"
              name="client"
              value={form.client}
              onChange={handleChange}
              disabled={loadingClientes}
              className={inputClass}
            >
              {loadingClientes && <option value="">Carregando...</option>}
              {clientes.map(c => (
                <option key={c.id} value={c.razao_social || c.nome}>
                  {c.razao_social || c.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Data de entrega + Horas estimadas */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-dueDate" className={labelClass}>
              Data de Entrega
            </label>
            <input
              id="ticket-dueDate"
              type="date"
              name="dueDate"
              value={form.dueDate}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ticket-estimatedHours" className={labelClass}>
              Horas Estimadas
            </label>
            <input
              id="ticket-estimatedHours"
              type="number"
              name="estimatedHours"
              value={form.estimatedHours}
              onChange={handleChange}
              min={0.5}
              step={0.5}
              className={inputClass}
            />
          </div>
        </div>

        {/* Botoes */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200/60 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-white px-5 py-2.5 text-sm font-medium text-gray-600 shadow-wa-apple transition-all hover:bg-white/80"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="hover-lift rounded-xl bg-wa-500 px-5 py-2.5 text-sm font-semibold text-white shadow-wa-apple transition-all hover:bg-wa-600"
          >
            Criar Ticket
          </button>
        </div>
      </form>
    </Modal>
  );
}
